<template>
    <div class="fac-areas-chart">
        <div class="fac-areas-chart__header">
            <div>
                <p class="fac-areas-chart__eyebrow">Infrastructure surface</p>
                <h3 class="fac-areas-chart__title">Campus area breakdown</h3>
                <p class="fac-areas-chart__subtitle">
                    Share of total built and landscape area across campus zones
                </p>
            </div>
            <div v-if="total > 0" class="fac-areas-chart__total">
                <span class="fac-areas-chart__total-label">Total surface</span>
                <span class="fac-areas-chart__total-value">{{ formatArea(total) }}</span>
                <span class="fac-areas-chart__total-unit">m²</span>
            </div>
        </div>

        <div v-if="!hasData" class="fac-areas-chart__empty">
            Campus areas not updated yet.
        </div>

        <div v-else class="fac-areas-chart__body">
            <div class="fac-areas-chart__donut">
                <apexchart
                    type="donut"
                    height="280"
                    :options="donutOptions"
                    :series="donutSeries"
                />
            </div>
            <div class="fac-areas-chart__bars">
                <apexchart
                    type="bar"
                    height="280"
                    :options="barOptions"
                    :series="barSeries"
                />
            </div>
        </div>
    </div>
</template>

<script>
var AREA_COLORS = ["#0f766e", "#b45309", "#d97706", "#57534e"];

export default {
    name: "FacilityAreasChart",
    props: {
        data: {
            type: Array,
            default: function () {
                return [];
            },
        },
    },
    data() {
        return {
            donutSeries: [],
            barSeries: [],
            donutOptions: {
                chart: {
                    type: "donut",
                    fontFamily: "inherit",
                    toolbar: { show: false },
                    animations: { enabled: true, speed: 550 },
                },
                labels: [],
                colors: AREA_COLORS,
                legend: { show: false },
                stroke: { width: 3, colors: ["#ffffff"] },
                dataLabels: {
                    enabled: true,
                    style: { fontSize: "11px", fontWeight: 700 },
                    dropShadow: { enabled: false },
                    formatter: function (val) {
                        return Math.round(val) + "%";
                    },
                },
                plotOptions: {
                    pie: {
                        donut: {
                            size: "68%",
                            labels: {
                                show: true,
                                name: {
                                    show: true,
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: "#78716c",
                                },
                                value: {
                                    show: true,
                                    fontSize: "18px",
                                    fontWeight: 800,
                                    color: "#1c1917",
                                    formatter: function (val) {
                                        return Number(val).toLocaleString("en-US");
                                    },
                                },
                                total: {
                                    show: true,
                                    label: "Total m²",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: "#78716c",
                                    formatter: function (w) {
                                        var sum = w.globals.seriesTotals.reduce(
                                            function (a, b) {
                                                return a + b;
                                            },
                                            0
                                        );
                                        return Number(sum).toLocaleString("en-US");
                                    },
                                },
                            },
                        },
                    },
                },
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return Number(val).toLocaleString("en-US") + " m²";
                        },
                    },
                },
            },
            barOptions: {
                chart: {
                    type: "bar",
                    fontFamily: "inherit",
                    toolbar: { show: false },
                    animations: { enabled: true, easing: "easeinout", speed: 500 },
                },
                colors: AREA_COLORS,
                plotOptions: {
                    bar: {
                        horizontal: true,
                        borderRadius: 8,
                        borderRadiusApplication: "end",
                        barHeight: "58%",
                        distributed: true,
                    },
                },
                dataLabels: {
                    enabled: true,
                    style: {
                        fontSize: "11px",
                        fontWeight: 700,
                        colors: ["#1c1917"],
                    },
                    formatter: function (val) {
                        return Number(val).toLocaleString("en-US") + " m²";
                    },
                    offsetX: 6,
                },
                legend: { show: false },
                grid: {
                    borderColor: "#f5f5f4",
                    strokeDashArray: 3,
                    xaxis: { lines: { show: true } },
                    yaxis: { lines: { show: false } },
                    padding: { left: 4, right: 16 },
                },
                xaxis: {
                    categories: [],
                    labels: {
                        style: { colors: "#78716c", fontSize: "11px" },
                        formatter: function (val) {
                            return Number(val).toLocaleString("en-US");
                        },
                    },
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                },
                yaxis: {
                    labels: {
                        style: {
                            colors: "#44403c",
                            fontSize: "12px",
                            fontWeight: 600,
                        },
                        maxWidth: 130,
                    },
                },
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return Number(val).toLocaleString("en-US") + " m²";
                        },
                    },
                },
            },
        };
    },
    computed: {
        hasData() {
            return (
                Array.isArray(this.data) &&
                this.data.some(function (item) {
                    return item && item.value != null && Number(item.value) > 0;
                })
            );
        },
        total() {
            if (!Array.isArray(this.data)) return 0;
            return this.data.reduce(function (sum, item) {
                return sum + (item && item.value != null ? Number(item.value) : 0);
            }, 0);
        },
    },
    watch: {
        data: {
            handler: function (rows) {
                if (!Array.isArray(rows)) return;
                var filtered = rows.filter(function (item) {
                    return item && item.value != null && Number(item.value) > 0;
                });
                var labels = filtered.map(function (item) {
                    return item.title;
                });
                var values = filtered.map(function (item) {
                    return Number(item.value);
                });

                this.donutSeries = values;
                this.barSeries = [{ name: "Area (m²)", data: values }];
                this.donutOptions = Object.assign({}, this.donutOptions, {
                    labels: labels,
                });
                this.barOptions = Object.assign({}, this.barOptions, {
                    xaxis: Object.assign({}, this.barOptions.xaxis, {
                        categories: labels,
                    }),
                });
            },
            immediate: true,
            deep: true,
        },
    },
    methods: {
        formatArea(val) {
            return Number(val || 0).toLocaleString("en-US");
        },
    },
};
</script>

<style lang="scss" scoped>
.fac-areas-chart {
    position: relative;
    overflow: hidden;
    background: linear-gradient(160deg, #ffffff 0%, #fffbeb 48%, #fafaf9 100%);
    border: 1px solid #fde68a;
    border-radius: 1.15rem;
    padding: 1.35rem 1.4rem 1.1rem;
    box-shadow: 0 10px 28px -18px rgba(180, 83, 9, 0.35);
}

.fac-areas-chart::before {
    content: "";
    position: absolute;
    top: -40px;
    right: -30px;
    width: 140px;
    height: 140px;
    border-radius: 9999px;
    background: radial-gradient(circle, rgba(251, 191, 36, 0.28) 0%, rgba(251, 191, 36, 0) 70%);
    pointer-events: none;
}

.fac-areas-chart__header {
    position: relative;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.35rem;
}

.fac-areas-chart__eyebrow {
    margin: 0;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #b45309;
}

.fac-areas-chart__title {
    margin: 0.2rem 0 0;
    font-size: 1.15rem;
    font-weight: 800;
    color: #1c1917;
}

.fac-areas-chart__subtitle {
    margin: 0.35rem 0 0;
    font-size: 0.82rem;
    color: #78716c;
    max-width: 28rem;
    line-height: 1.4;
}

.fac-areas-chart__total {
    flex-shrink: 0;
    text-align: right;
    padding: 0.55rem 0.75rem;
    border-radius: 0.85rem;
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid #fde68a;
}

.fac-areas-chart__total-label {
    display: block;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #a8a29e;
}

.fac-areas-chart__total-value {
    display: inline-block;
    margin-top: 0.15rem;
    font-size: 1.15rem;
    font-weight: 800;
    color: #1c1917;
    line-height: 1.1;
}

.fac-areas-chart__total-unit {
    margin-left: 0.2rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: #78716c;
}

.fac-areas-chart__body {
    position: relative;
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

@media (min-width: 1024px) {
    .fac-areas-chart__body {
        grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
        align-items: center;
    }
}

.fac-areas-chart__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 16rem;
    color: #a8a29e;
    font-size: 0.9rem;
}
</style>
